// game.v1 wire types, hand-written to match contracts/ (JSON Schema is the source of truth).
// They will be generated from the schemas later.

export const PROTOCOL = "game.v1";

export type JsonObject = Record<string, unknown>;

export type ErrorCode =
  | "bad_request"
  | "conflict"
  | "unknown_type"
  | "not_authenticated"
  | "already_authenticated"
  | "wrong_env"
  | "client_outdated"
  | "maintenance"
  | "rate_limited"
  | "internal";

export interface ErrorBody {
  code: ErrorCode;
  /** For logs only, never shown to players. */
  message: string;
  details?: JsonObject;
}

export interface RequestFrame {
  id: string;
  type: string;
  payload?: JsonObject;
}

export interface ResponseOkFrame {
  id: string;
  type: string;
  ok: true;
  payload?: JsonObject;
}

export interface ResponseErrorFrame {
  id: string;
  type: string;
  ok: false;
  error: ErrorBody;
}

export interface PushFrame {
  type: string;
  payload: JsonObject;
}

export type ServerFrame = ResponseOkFrame | ResponseErrorFrame | PushFrame;

export const CloseCode = {
  goingAway: 1001,
  malformed: 4000,
  authTimeout: 4001,
  replaced: 4009,
  clientOutdated: 4010,
  rateLimited: 4029,
  wrongEnv: 4307,
  maintenance: 4503,
} as const;

/** Close codes after which the client must not reconnect on its own. */
export const NO_RECONNECT_CODES: readonly number[] = [
  CloseCode.replaced,
  CloseCode.clientOutdated,
];

export interface Hello {
  protocol: typeof PROTOCOL;
  serverTime: number;
  heartbeatMs: number;
  authTimeoutMs: number;
  maxMessageBytes: number;
  /** Largest `state.save` data, as JSON bytes (older servers don't send it). */
  maxStateBytes?: number;
  /** The env serving this connection. */
  env: string;
}

export interface ClientInfo {
  version: string;
  build: number;
  platform: "ios" | "android" | "web";
  os?: string;
  model?: string;
  locale?: string;
}

/** `deviceId` is this device's UUIDv7 (see platform/deviceId.ts); its first login creates the player. */
export interface AuthLoginRequest {
  deviceId: string;
  client: ClientInfo;
}

/**
 * `playerId` is `<prefix>_<10 chars>`; `isNew` is true on the device's first login. `env` is the player's
 * pinned env; the client stores it and connects with `?env=` from then on.
 */
export interface AuthLoginResponse {
  playerId: string;
  sessionId: string;
  isNew: boolean;
  env: string;
}

/** error.details when error.code is wrong_env: the player's pinned env. */
export interface WrongEnvDetails {
  env: string;
}

export interface ClientOutdatedDetails {
  minVersion: string;
  storeUrl?: string;
}

export interface MaintenanceDetails {
  until?: number;
}

/** Namespaces (economy, mechanics, liveops, ...) mapped to their objects. */
export type ConfigSection = Record<string, JsonObject>;

/** One downloadable file. Files are immutable: a changed file gets a new url and sha256. */
export interface ContentAsset {
  /** Name the game looks the asset up by, unique within its bundle. */
  alias: string;
  /** Absolute CDN URL. */
  url: string;
  /** Hex SHA-256 of the file. */
  sha256: string;
  bytes: number;
  contentType: string;
}

/** OTA content of a release: named bundles of assets (Pixi Assets bundles). */
export interface Content {
  bundles: Record<string, ContentAsset[]>;
}

export interface ClientConfig {
  version: number;
  publishedAt: string;
  data: ConfigSection;
  content: Content;
}

export interface SessionBootstrapResponse {
  player: { id: string; createdAt: string };
  state: { version: number; data: JsonObject };
  config: ClientConfig;
  serverTime: number;
}

export interface PingResponse {
  serverTime: number;
}

export interface ConfigUpdatedPush {
  version: number;
}

export type AnalyticsValue = string | number | boolean | null;

/** Flat: at most 32 snake_case keys, strings of at most 256 chars. */
export type AnalyticsProps = Record<string, AnalyticsValue>;

export interface AnalyticsEvent {
  /** Unique per event, `^[A-Za-z0-9_-]{8,64}$`, so a resent batch can be deduplicated. */
  id: string;
  /** snake_case; session_start and session_end are reserved for the server. */
  name: string;
  /** Client clock, Unix ms. */
  ts: number;
  props?: AnalyticsProps;
}

/** Only after auth.login. 1 to 50 events. */
export interface AnalyticsTrackRequest {
  events: AnalyticsEvent[];
}

export interface AnalyticsTrackResponse {
  accepted: number;
}

export type Empty = Record<string, never>;

/** Request type -> request payload and response payload. */
export interface Requests {
  ping: { request: Empty; response: PingResponse };
  "auth.login": { request: AuthLoginRequest; response: AuthLoginResponse };
  "session.bootstrap": { request: Empty; response: SessionBootstrapResponse };
  "config.get": { request: Empty; response: ClientConfig };
  "analytics.track": { request: AnalyticsTrackRequest; response: AnalyticsTrackResponse };
  "state.save": { request: StateSaveRequest; response: StateSaveResponse };
}

/** state.save: version must be the stored version + 1 (contracts/ws/state.save.schema.json). */
export interface StateSaveRequest {
  version: number;
  data: JsonObject;
}

export interface StateSaveResponse {
  version: number;
}

/** Push type -> payload. */
export interface Pushes {
  hello: Hello;
  "config.updated": ConfigUpdatedPush;
}

export type RequestType = keyof Requests;
export type PushType = keyof Pushes;
